import { AuthenticationResult } from '@azure/msal-browser'
import { jwtDecode, JwtPayload } from 'jwt-decode'

export interface AccessTokenClaims extends JwtPayload {
  name?: string
  preferred_username?: string
  oid?: string
  roles?: string[]
}

// Decode JWT token payload
export const decodeToken = (token: string): AccessTokenClaims | null => {
  try {
    return jwtDecode<AccessTokenClaims>(token)
  } catch (error) {
    console.error('[Token] Failed to decode token:', error)
    return null
  }
}

// Entra ID puts the user's app roles in the `roles` claim
export const getRolesFromToken = (token: string | null | undefined): string[] => {
  if (!token) return []
  return decodeToken(token)?.roles ?? []
}

// Get token expiration details for logging/debugging
export const getTokenExpirationInfo = (
  token: string,
): {
  expiresAt: Date | null
  expiresInMinutes: number
} => {
  try {
    if (!token) {
      return {
        expiresAt: null,
        expiresInMinutes: 0,
      }
    }

    const decoded = decodeToken(token)

    if (!decoded?.exp) {
      return {
        expiresAt: null,
        expiresInMinutes: 0,
      }
    }

    const expirationTime = decoded.exp * 1000
    const expiresAt = new Date(expirationTime)

    const expiresInMinutes = Math.floor((expirationTime - Date.now()) / 1000 / 60)

    return {
      expiresAt,
      expiresInMinutes,
    }
  } catch (error) {
    console.error('[Token] Failed to get expiration info:', error)

    return {
      expiresAt: null,
      expiresInMinutes: 0,
    }
  }
}

// Optional helper for logging MSAL responses
export const logTokenInfo = (response: AuthenticationResult): void => {
  if (!response.accessToken) {
    console.warn('[Token] No access token found')
    return
  }

  const { expiresAt, expiresInMinutes } = getTokenExpirationInfo(response.accessToken)

  console.log(
    `[Token] Access token valid for ${expiresInMinutes} minute(s), expires at ${
      expiresAt?.toLocaleString() ?? 'unknown'
    }`,
  )
}
